import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { useCompanyPuc } from "@/lib/api/puc.queries";
import { useTerceros } from "@/lib/api/terceros.queries";
import {
  useDeleteOpeningBalanceDraft,
  useOpeningBalances,
  usePostOpeningBalanceDraft,
  useSaveOpeningBalanceDraft,
} from "@/lib/api/opening-balances.queries";
import { useActiveCompany } from "@/lib/company-context";
import { isStaleDraftError, translateDraftError } from "@/lib/draft-errors";
import { useTeamPermission } from "@/lib/team-context";
import { fmtDate } from "@/components/inventory/format";

export const Route = createFileRoute(
  "/companies/$companyId/contabilidad/saldos-iniciales",
)({
  component: SaldosInicialesPage,
});

type LineState = {
  id: string;
  accountCode: string;
  accountNameOverride: string;
  terceroId: string;
  debit: string;
  credit: string;
  note: string;
};

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function newLine(): LineState {
  return {
    id: crypto.randomUUID(),
    accountCode: "",
    accountNameOverride: "",
    terceroId: "",
    debit: "",
    credit: "",
    note: "",
  };
}

function parseAmount(value: string): number {
  const raw = value
    .trim()
    .replace(/\s/g, "")
    .replace(/\$/g, "")
    .replace(/COP/gi, "")
    .replace(/[^0-9,.-]/g, "");
  if (!raw) return 0;

  const commaCount = raw.split(",").length - 1;
  const dotCount = raw.split(".").length - 1;
  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");
  let normalized = raw;

  if (commaCount > 0 && dotCount > 0) {
    normalized =
      lastComma > lastDot
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw.replace(/,/g, "");
  } else if (commaCount > 0) {
    const decimals = raw.length - lastComma - 1;
    normalized =
      commaCount > 1 || decimals === 3
        ? raw.replace(/,/g, "")
        : raw.replace(",", ".");
  } else if (dotCount > 0) {
    const decimals = raw.length - lastDot - 1;
    normalized =
      dotCount > 1 || decimals === 3 ? raw.replace(/\./g, "") : raw;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function formatInputAmount(value: number): string {
  return value ? String(value) : "";
}

function SaldosInicialesPage() {
  return (
    <RequireAuth>
      <AppShell>
        <SaldosInicialesContent />
      </AppShell>
    </RequireAuth>
  );
}

function SaldosInicialesContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const overviewQuery = useOpeningBalances(companyId);
  const pucQuery = useCompanyPuc(companyId, { onlyActive: true });
  const tercerosQuery = useTerceros(companyId, { status: "active", pageSize: 200 });

  const saveDraft = useSaveOpeningBalanceDraft(companyId);
  const postDraft = usePostOpeningBalanceDraft(companyId);
  const discardDraft = useDeleteOpeningBalanceDraft(companyId);
  const { t } = useTranslation();
  const canWrite = useTeamPermission("accounting.write");
  const [discardOpen, setDiscardOpen] = useState(false);

  const [hydratedId, setHydratedId] = useState<string | null>(null);
  const [cutoverDate, setCutoverDate] = useState(todayIsoDate());
  const [sourceSystem, setSourceSystem] = useState("");
  const [notes, setNotes] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [lines, setLines] = useState<LineState[]>([newLine(), newLine()]);

  const draft = overviewQuery.data?.draft ?? null;
  const posted = overviewQuery.data?.posted ?? null;

  useEffect(() => {
    if (!draft || hydratedId === draft.id) return;
    setHydratedId(draft.id);
    setCutoverDate(draft.cutoverDate);
    setSourceSystem(draft.sourceSystem ?? "");
    setNotes(draft.notes ?? "");
    setLines(
      draft.lines.length
        ? draft.lines.map((line) => ({
            id: line.id,
            accountCode: line.accountCode,
            accountNameOverride: line.accountName,
            terceroId: line.terceroId ?? "",
            debit: formatInputAmount(line.debit),
            credit: formatInputAmount(line.credit),
            note: line.note ?? "",
          }))
        : [newLine(), newLine()],
    );
  }, [draft, hydratedId]);

  const accounts = useMemo(
    () =>
      (pucQuery.data?.accounts ?? [])
        .filter((account) => account.is_active && !account.is_summary)
        .slice(0, 1000),
    [pucQuery.data],
  );
  const terceros = tercerosQuery.data?.terceros ?? [];

  const totals = useMemo(() => {
    const debit = lines.reduce((sum, line) => sum + parseAmount(line.debit), 0);
    const credit = lines.reduce((sum, line) => sum + parseAmount(line.credit), 0);
    return {
      debit,
      credit,
      difference: Math.round((debit - credit + Number.EPSILON) * 100) / 100,
    };
  }, [lines]);
  const balanced = Math.abs(totals.difference) <= 0.009 && totals.debit > 0;

  function updateLine(id: string, patch: Partial<LineState>) {
    setLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        const next = { ...line, ...patch };
        if (patch.debit !== undefined && parseAmount(patch.debit) > 0) {
          next.credit = "";
        }
        if (patch.credit !== undefined && parseAmount(patch.credit) > 0) {
          next.debit = "";
        }
        return next;
      }),
    );
  }

  function addLine() {
    setLines((prev) => [...prev, newLine()]);
  }

  function removeLine(id: string) {
    setLines((prev) => (prev.length <= 2 ? prev : prev.filter((line) => line.id !== id)));
  }

  function applyPaste() {
    const parsed = pasteText
      .split(/\r?\n/)
      .map((row) => row.trim())
      .filter(Boolean)
      .map((row) => {
        const cols = row.includes("\t")
          ? row.split("\t")
          : row.includes(";")
            ? row.split(";")
            : row.split(",");
        return {
          id: crypto.randomUUID(),
          accountCode: (cols[0] ?? "").trim(),
          accountNameOverride: (cols[1] ?? "").trim(),
          terceroId: (cols[4] ?? "").trim(),
          debit: (cols[2] ?? "").trim(),
          credit: (cols[3] ?? "").trim(),
          note: (cols[5] ?? "").trim(),
        };
      })
      .filter((line) => line.accountCode);
    if (!parsed.length) {
      toast.error("No encontramos filas para importar");
      return;
    }
    setLines(parsed);
    setPasteText("");
    toast.success("Filas cargadas");
  }

  /** Reload the saved draft into the form, dropping what this editor typed (after someone saved first). */
  async function reloadDraft() {
    await overviewQuery.refetch();
    setHydratedId(null);
  }

  /** A stale draft says so and offers the reload; any other failure shows its own message. */
  function reportFailure(title: string, err: unknown) {
    const draftMessage = translateDraftError(err, t);
    if (draftMessage) {
      toast.error(draftMessage, isStaleDraftError(err)
        ? { action: { label: "Recargar borrador", onClick: () => void reloadDraft() } }
        : undefined);
      return;
    }
    toast.error(title, { description: err instanceof Error ? err.message : "Error inesperado" });
  }

  function buildPayload() {
    return {
      // The revision this form was opened (or last saved) at: a draft saved by someone else since is refused.
      ...(draft ? { expectedRevision: draft.revision } : {}),
      cutoverDate,
      sourceSystem: sourceSystem.trim() || undefined,
      notes: notes.trim() || undefined,
      lines: lines
        .filter((line) => line.accountCode.trim())
        .map((line) => ({
          accountCode: line.accountCode.trim(),
          accountNameOverride: line.accountNameOverride.trim() || undefined,
          terceroId: line.terceroId || undefined,
          debit: parseAmount(line.debit),
          credit: parseAmount(line.credit),
          note: line.note.trim() || undefined,
        })),
    };
  }

  function submitDraft(e: FormEvent) {
    e.preventDefault();
    saveDraft.mutate(buildPayload(), {
      onSuccess: (saved) => {
        setHydratedId(saved.id);
        toast.success("Saldos iniciales guardados");
      },
      onError: (err) => reportFailure("No pudimos guardar", err),
    });
  }

  async function post() {
    try {
      const saved = await saveDraft.mutateAsync(buildPayload());
      setHydratedId(saved.id);
      // Post exactly what was just saved: an edit by someone else in between is refused.
      const postedBatch = await postDraft.mutateAsync({ expectedRevision: saved.revision });
      toast.success("Saldos iniciales posteados", {
        description: postedBatch.journalEntryId ?? undefined,
      });
    } catch (err) {
      reportFailure("No pudimos postear", err);
    }
  }

  async function discard() {
    try {
      await discardDraft.mutateAsync({ expectedRevision: draft?.revision });
      setDiscardOpen(false);
      setHydratedId(null);
      setCutoverDate(todayIsoDate());
      setSourceSystem("");
      setNotes("");
      setLines([newLine(), newLine()]);
      toast.success("Borrador descartado");
    } catch (err) {
      setDiscardOpen(false);
      reportFailure("No pudimos descartar el borrador", err);
    }
  }

  if (overviewQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (overviewQuery.isError) {
    return (
      <ErrorState
        title="No pudimos cargar los saldos iniciales"
        description={overviewQuery.error?.message ?? ""}
        onRetry={() => void overviewQuery.refetch()}
        retryLabel="Reintentar"
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/contabilidad"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a Contabilidad
          </Link>
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Saldos iniciales
            </h1>
            {posted ? <Badge variant="success">Posteados</Badge> : null}
          </div>
        </div>
        {posted?.journalEntryId ? (
          <Button asChild variant="outline">
            <Link
              to="/companies/$companyId/contabilidad/diario/$entryId"
              params={{ companyId, entryId: posted.journalEntryId }}
            >
              <CheckCircle2 className="h-4 w-4" />
              Ver asiento
            </Link>
          </Button>
        ) : null}
      </header>

      {posted ? (
        <PostedSummary posted={posted} />
      ) : (
        <form onSubmit={submitDraft} className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Corte de migracion</CardTitle>
              <CardDescription>
                Base contable inicial para continuar operaciones en PahFacturar.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-[180px_1fr]">
              <div className="grid gap-1.5">
                <Label htmlFor="cutoverDate">Fecha de corte</Label>
                <Input
                  id="cutoverDate"
                  type="date"
                  value={cutoverDate}
                  onChange={(e) => setCutoverDate(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="sourceSystem">Sistema anterior</Label>
                <Input
                  id="sourceSystem"
                  value={sourceSystem}
                  onChange={(e) => setSourceSystem(e.target.value)}
                  placeholder="Siigo, World Office, Helisa..."
                />
              </div>
              <div className="grid gap-1.5 md:col-span-2">
                <Label htmlFor="notes">Notas</Label>
                <Input
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Acta de migracion, balance de prueba, corte..."
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Lineas contables</CardTitle>
                  <CardDescription>
                    Cuenta, tercero opcional y saldo debito o credito.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={addLine}>
                    <Plus className="h-4 w-4" />
                    Linea
                  </Button>
                  {draft && canWrite ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={discardDraft.isPending || saveDraft.isPending || postDraft.isPending}
                      onClick={() => setDiscardOpen(true)}
                    >
                      <Trash2 className="h-4 w-4" />
                      Descartar borrador
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    disabled={!canWrite || saveDraft.isPending || !cutoverDate}
                    variant="outline"
                  >
                    {saveDraft.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    Guardar
                  </Button>
                  <Button
                    type="button"
                    disabled={
                      !canWrite ||
                      postDraft.isPending ||
                      saveDraft.isPending ||
                      !cutoverDate ||
                      !balanced
                    }
                    onClick={post}
                  >
                    {postDraft.isPending || saveDraft.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    Postear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <datalist id="opening-balance-accounts">
                {accounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.name}
                  </option>
                ))}
              </datalist>

              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <textarea
                  className="min-h-20 rounded-md border bg-background px-3 py-2 text-sm"
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="130505\tClientes nacionales\t1000000\t\t\tSaldo cartera"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!pasteText.trim()}
                  onClick={applyPaste}
                >
                  <Upload className="h-4 w-4" />
                  Cargar
                </Button>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cuenta</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Tercero</TableHead>
                      <TableHead className="text-right">Debito</TableHead>
                      <TableHead className="text-right">Credito</TableHead>
                      <TableHead>Nota</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>
                          <Input
                            className="w-32 font-mono text-xs"
                            list="opening-balance-accounts"
                            value={line.accountCode}
                            onChange={(e) =>
                              updateLine(line.id, {
                                accountCode: e.target.value,
                              })
                            }
                            placeholder="130505"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="min-w-52"
                            value={line.accountNameOverride}
                            onChange={(e) =>
                              updateLine(line.id, {
                                accountNameOverride: e.target.value,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <select
                            className="h-9 min-w-52 rounded-md border bg-background px-2 text-sm"
                            value={line.terceroId}
                            onChange={(e) =>
                              updateLine(line.id, { terceroId: e.target.value })
                            }
                          >
                            <option value="">Sin tercero</option>
                            {terceros.map((tercero) => (
                              <option key={tercero.id} value={tercero.id}>
                                {tercero.legalName}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-36 text-right font-mono text-xs"
                            value={line.debit}
                            onChange={(e) =>
                              updateLine(line.id, { debit: e.target.value })
                            }
                            disabled={parseAmount(line.credit) > 0}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="w-36 text-right font-mono text-xs"
                            value={line.credit}
                            onChange={(e) =>
                              updateLine(line.id, { credit: e.target.value })
                            }
                            disabled={parseAmount(line.debit) > 0}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="min-w-48"
                            value={line.note}
                            onChange={(e) =>
                              updateLine(line.id, { note: e.target.value })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={lines.length <= 2}
                            onClick={() => removeLine(line.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <TotalBox label="Debito" value={totals.debit} />
                <TotalBox label="Credito" value={totals.credit} />
                <TotalBox
                  label="Diferencia"
                  value={totals.difference}
                  tone={balanced ? "success" : "danger"}
                />
              </div>
            </CardContent>
          </Card>
        </form>
      )}

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="¿Descartar el borrador de saldos iniciales?"
        description="Se elimina el borrador y sus líneas. No se ha posteado nada en el diario. Esta acción no se puede deshacer."
        pending={discardDraft.isPending}
        onConfirm={() => void discard()}
      />
    </div>
  );
}

function TotalBox(props: {
  label: string;
  value: number;
  tone?: "success" | "danger";
}) {
  const fmtCop = useMoneyFormatter();
  return (
    <div className="rounded-md border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{props.label}</p>
      <p
        className={[
          "mt-1 text-lg font-semibold",
          props.tone === "success" ? "text-emerald-700" : "",
          props.tone === "danger" && Math.abs(props.value) > 0.009
            ? "text-destructive"
            : "",
        ].join(" ")}
      >
        {fmtCop(props.value)}
      </p>
    </div>
  );
}

function PostedSummary(props: {
  posted: {
    cutoverDate: string;
    sourceSystem: string | null;
    totalDebit: number;
    totalCredit: number;
    difference: number;
    lineCount: number;
    postedAt: string | null;
    lines: Array<{
      id: string;
      accountCode: string;
      accountName: string;
      terceroId: string | null;
      debit: number;
      credit: number;
      note: string | null;
    }>;
  };
}) {
  const fmtCop = useMoneyFormatter();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Corte {fmtDate(props.posted.cutoverDate)}
        </CardTitle>
        <CardDescription>
          {props.posted.sourceSystem ?? "Migracion"} · {props.posted.lineCount} lineas
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <TotalBox label="Debito" value={props.posted.totalDebit} />
          <TotalBox label="Credito" value={props.posted.totalCredit} />
          <TotalBox
            label="Diferencia"
            value={props.posted.difference}
            tone="success"
          />
        </div>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cuenta</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="text-right">Debito</TableHead>
                <TableHead className="text-right">Credito</TableHead>
                <TableHead>Nota</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {props.posted.lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell className="font-mono text-xs">
                    {line.accountCode}
                  </TableCell>
                  <TableCell>{line.accountName}</TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtCop(line.debit)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtCop(line.credit)}
                  </TableCell>
                  <TableCell>{line.note ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
